import {
  params,
  shape as shapeType,
  string as stringType,
  bool as boolType,
  id as idType,
  number as numberType,
} from '@thd-nucleus/data-sources';

/**
 * `militaryDiscountDetails(userId: ID!, customerAccountId: ID!, channelId: Int!)`
 *
 * Enrollment as the BFF holds it, replacing this experience's
 * `GET /{userId}/customer/{custAccountId}/militaryDiscount/enrollment` call. Not
 * a different answer, and not a second implementation: one
 * `MilitaryDiscountService.getMilitaryDiscountEnrollmentDetails()` sits behind
 * both entry points and one adapter maps the same DTO to each, so this is the
 * REST payload with two fields renamed and the profile's name nested.
 *
 * The REST payload's `enrollmentStatus: 'Y' | 'N' | 'P'` arrives split in two:
 * `isEnrolled` is the BFF's own `'Y'` test, and `enrollmentStatus` carries the
 * whole answer. Only the latter can tell `'N'` from `'P'`, which is why both are
 * selected - see the SheerID handoff in pages/LandingPage.tsx.
 *
 * `enrollmentStatus`, and `militarySubgroup`/`militaryBranch`/`isSpouse` on the
 * profile, as of ACC3-750. That change renamed `militaryDiscountStatus` and
 * deleted `EnrolledProfile.militaryStatus` along with the `MilitaryStatus` enum
 * behind it, so the shape this file used to describe - and the note here saying
 * the published contract's SDL block was ahead of the as-built schema - were
 * both true right up until they were exactly backwards. A selection set naming a
 * field the schema does not define fails the whole query rather than that field,
 * which is what this looked like in the wild: `Validation error of type
 * FieldUndefined`, and no data at all.
 *
 * @see https://github.com/one-thd/ocm_customer_account_b2bmyaccount/pull/4084
 * @see https://thd.atlassian.net/wiki/spaces/CAE/pages/4087611670/API+Contracts+-+Military+Discount+ID.me+integration
 */
export const militaryDiscountDetailsDataModel = {
  militaryDiscountDetails: params({
    userId: idType().isRequired(),
    customerAccountId: idType().isRequired(),
    channelId: numberType().isRequired(),
  }).shape({
    isEnrolled: boolType(),
    /* `EnrollmentStatusEnum` is an enum server-side, but a leaf selection writes
    /* the same document either way - so the value this experience branches on
    /* lives in constants/enrollment-status.ts rather than a customType().enum()
    /* here. Same call the military verification component makes for
    /* `militarySubgroup`. */
    enrollmentStatus: stringType(),
    enrolledProfile: shapeType({
      /* `V2Name`, where the REST DTO carried `firstName`/`lastName` flat on the
      /* profile. Nothing on the page reads them yet - see the note in
      /* ProgramSection - but they are what the REST call returned, and leaving
      /* them out would narrow the payload without anyone deciding to. */
      name: shapeType({
        firstName: stringType(),
        lastName: stringType(),
      }),
      companyName: stringType(),
      /* ID.me's own subgroup string ("Veteran", "Service Member", "Retiree",
      /* "Military Spouse", "Surviving Spouse"), the branch behind it, and the
      /* spouse flag that used to be inferrable from the old enum. Selected for
      /* the same reason `name` is: these are what the REST payload carried, and
      /* dropping them would narrow it without anyone deciding to. */
      militarySubgroup: stringType(),
      militaryBranch: stringType(),
      militaryUuid: stringType(),
      verificationDate: stringType(),
      isSpouse: boolType(),
    }),
    /* The legacy SheerID application id, still selected because the pre-ID.me
    /* flow is still switchable: a customer with an application open and no
    /* enrollment is put straight back into the iframe. */
    verificationId: stringType(),
    isMilitaryDiscountCapped: boolType(),
  }),
};
